package com.yendo.mx.models

import kotlinx.serialization.SerialName
import kotlinx.serialization.Serializable

@Serializable
data class Producto(
    val id: String = "", // ID del documento generado por Firestore

    @SerialName("id_negocio")
    val idNegocio: String = "",

    @SerialName("id_zona")
    val idZona: String = "",

    @SerialName("id_producto_global")
    val idProductoGlobal: String = "", // 🚀 ESTA ES LA LLAVE DEL MODELO HÍBRIDO

    val destacado: Boolean = false, // 🚀 NUEVO: Sistema de Pines (Destacados)

    @SerialName("info_producto")
    val infoProducto: InfoProducto = InfoProducto(),

    val operacion: OperacionProducto = OperacionProducto(),

    val adicionales: List<GrupoAdicional> = emptyList(),

    // 🚀 ATRIBUTOS BFF NUBE (0ms)
    @SerialName("nombre_restaurante")
    val nombreRestaurante: String = "",

    @SerialName("logo_restaurante")
    val logoRestaurante: String = "",

    // 🚀 NUEVA LISTA: PRECIOS DE MAYOREO (B2B)
    @SerialName("escalas_precio")
    val escalasPrecio: List<EscalaPrecio> = emptyList()
) {
    // 🚀 PROPIEDADES CALCULADAS: Atajo que mapea directamente idNegocio
    val idRestaurante: String get() = idNegocio
    val marca: String get() = infoProducto.marca.trim()

    // 🚀 LIMPIEZA AUTOMÁTICA DE NOMBRE: Remueve la marca si está duplicada/concatenada en el nombre
    val nombre: String get() {
        val rawNombre = infoProducto.nombre.trim()
        val rawMarca = infoProducto.marca.trim()

        if (rawMarca.isNotBlank()) {
            val regexConSeparador = Regex("^${Regex.escape(rawMarca)}\\s*[:-|]?\\s*", RegexOption.IGNORE_CASE)
            if (regexConSeparador.containsMatchIn(rawNombre)) {
                val limpio = rawNombre.replace(regexConSeparador, "").trim()
                if (limpio.isNotBlank()) return limpio
            }
        }

        if (rawNombre.contains(" - ")) {
            val partes = rawNombre.split(" - ", limit = 2)
            if (partes.size == 2 && partes[0].isNotBlank() && partes[1].isNotBlank()) {
                if (rawMarca.isBlank() || partes[0].equals(rawMarca, ignoreCase = true)) {
                    return partes[1].trim()
                }
            }
        } else if (rawNombre.contains(" | ")) {
            val partes = rawNombre.split(" | ", limit = 2)
            if (partes.size == 2 && partes[0].isNotBlank() && partes[1].isNotBlank()) {
                if (rawMarca.isBlank() || partes[0].equals(rawMarca, ignoreCase = true)) {
                    return partes[1].trim()
                }
            }
        }

        return rawNombre
    }

    val descripcion: String get() = infoProducto.descripcion
    val precio: Double get() = infoProducto.precio
    val foto: String get() = infoProducto.foto
    val categoria: String get() = infoProducto.categoria
    val disponible: Boolean get() = operacion.disponible

    // 🚀 NUEVOS ATAJOS CALCULADOS PARA SUPERMERCADO Y COMERCIOS
    val tipoVenta: String get() = infoProducto.tipoVenta
    val precioPor: String get() = infoProducto.precioPor
    val pesoEstimadoKg: Double get() = infoProducto.pesoEstimadoKg
}

@Serializable
data class InfoProducto(
    val categoria: String = "",
    val descripcion: String = "",
    val foto: String = "",
    val nombre: String = "",
    val marca: String = "", // 🚀 MARCA DEL PRODUCTO (Firestore / Algolia)
    val precio: Double = 0.0,

    // 🚀 NUEVAS VARIABLES PARA PROMOCIONES (Precios Tachados y Etiquetas)
    @SerialName("precio_original")
    val precioOriginal: Double = 0.0,

    @SerialName("tipo_promocion")
    val tipoPromocion: String = "NINGUNA", // Ej: NINGUNA, DOS_POR_UNO, DESCUENTO

    // 🚀 NUEVAS VARIABLES PARA SUPERMERCADO (Frutas, Verduras, Farmacia)
    @SerialName("tipo_venta")
    val tipoVenta: String = "UNIDAD", // "UNIDAD", "PESO", "PIEZA_POR_PESO"

    @SerialName("precio_por")
    val precioPor: String = "PIEZA", // "PIEZA", "KG", "GRAMO"

    @SerialName("peso_estimado_kg")
    val pesoEstimadoKg: Double = 0.0
)

@Serializable
data class OperacionProducto(
    val disponible: Boolean = true
)

@Serializable
data class GrupoAdicional(
    @SerialName("nombre_grupo")
    val nombreGrupo: String = "",

    @SerialName("limite_seleccion")
    val limiteSeleccion: Int = 1, // El MÁXIMO que pueden elegir

    @SerialName("obligatorio")
    val minimoSeleccion: Int = 0, // 🚀 Tu lógica: 1 o más para ser obligatorio

    // 🚀 EL SWITCH MÁGICO PARA COMIDA CHINA (Permite pedir "2 de pollo")
    @SerialName("permite_repeticion")
    val permiteRepeticion: Boolean = false,

    val opciones: List<OpcionAdicional> = emptyList()
) {
    // Atajo para lógica visual en el selector
    val esObligatorio: Boolean get() = minimoSeleccion > 0
}

@Serializable
data class OpcionAdicional(
    val nombre: String = "",
    @SerialName("precio_extra")
    val precioExtra: Double = 0.0,
    // 🚀 NUEVO: Control de stock individual por ingrediente/adicional
    val disponible: Boolean = true,
    // 🚀 INYECTADO: Este campo viaja "oculto" desde el menú hasta el carrito
    var nombre_grupo: String = "",
    // 🚀 CORRECCIÓN: Debe llamarse 'foto' para coincidir con Firebase
    val foto: String = ""
)

// 🚀 NUEVO MODELO PARA ESCALAS DE MAYOREO
@Serializable
data class EscalaPrecio(
    @SerialName("cantidad_minima")
    val cantidadMinima: Int = 0,

    @SerialName("cantidad_maxima")
    val cantidadMaxima: Int = Int.MAX_VALUE, // 🚀 NUEVO: Rango superior

    @SerialName("precio_unitario")
    val precioUnitario: Double = 0.0
)